import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { FC, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Globe } from "@/assets/icons/ds";
import { Button, ButtonLink } from "@/components/Interface/Button/Button";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { CheckoutPaymentCard } from "@/components/Layout/Payment/CheckoutPaymentCard/CheckoutPaymentCard";
import { LightboxWrapper } from "@/components/Layout/Payment/LightboxWrapper/LightboxWrapper";
import {
  DeductionRow,
  DiscountLabel,
  DueNowTotalRow,
} from "@/components/Layout/PriceTable/DueNowRows";
import { PriceRow } from "@/components/Layout/PriceTable/PriceRow";
import {
  SubscriptionInfoBox,
  SubscriptionInfoRow,
} from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { useOptionCheckoutSubmit } from "@/components/Portal/DeviceManagement/hooks/useOptionCheckoutSubmit";
import { RoamingBookingPage } from "@/components/Portal/DeviceManagement/Roaming/components/RoamingBookingPage";
import {
  isYearlyRoamingPackage,
  optionBasketAtom,
} from "@/components/Portal/DeviceManagement/Roaming/roamingUtils";
import { useAlert } from "@/hooks/useAlert";
import { useGetCampaignEndDate } from "@/hooks/useGetCampaignEndDate";
import { useHandleCheckoutError } from "@/hooks/useHandleCheckoutError";
import { useHandleError } from "@/hooks/useHandleError";
import { usePaymentMode } from "@/hooks/usePaymentMode";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import {
  orderRoamingPackage,
  useGetAvailableSunriseRoamingPackages,
} from "@/services/api";
import {
  DrCreditCardCardType,
  GetAvailableSunriseRoamingPackagesPackageType,
} from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import { getCreditAmount } from "@/utils/basketUtils";
import { getFlatNameFromDevice, stripFlatName } from "@/utils/deviceUtils";
import {
  capitalizeFirstLetter,
  getCurrency,
  getCurrencyFromLookupValue,
} from "@/utils/translationHelpers";

type FormProps = {
  paymentProvider: DrCreditCardCardType;
};

/**
 * Checkout step for a selected roaming package.
 *
 * Renders the order summary, payment method card, and price breakdown.
 * Submits via Datatrans lightbox (3DS), direct credit-card charge, or invoice.
 * Reads the pending package basket from `optionBasketAtom` (localStorage-backed).
 * Clears the atom and navigates to `../display` on success or cancellation.
 * Redirects to `../booking` when the atom is stale (package no longer in the available list)
 * or when the package list fails to load.
 */
export const CheckoutRoamingOption: FC = () => {
  const [optionBasket, setOptionBasket] = useAtom(optionBasketAtom);
  const [simId] = useSimIdFromQueryParam();
  const [paymentStatus] = useQueryParam("payment");
  const { t } = useTranslation();
  const { t: tCheckout } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.roaming.checkout",
  });
  const showAlert = useAlert();
  const queryClient = useQueryClient();
  const handleError = useHandleError();
  const navigate = useNavigate();

  const { data: simDetail } = useDisplayedSim();
  const {
    data: availableRoamingPackages,
    isError: isPackagesError,
    isPending: isLoadingPackages,
  } = useGetAvailableSunriseRoamingPackages(Number(simId), {
    packageType: GetAvailableSunriseRoamingPackagesPackageType.Data_Roaming,
  });

  const entry = optionBasket?.entries[0];
  const totalOnceAmount = optionBasket?.total?.oneTime?.amount;
  const option = entry?.options?.length ? entry?.options[0] : undefined;
  const optionId = option?.product?.id ?? -1;

  const selectedPackage =
    availableRoamingPackages?.subscriberPackagesRoaming &&
    availableRoamingPackages.subscriberPackagesRoaming.find((pack) => {
      return pack.id === optionId;
    });

  const isYearlyPackage = isYearlyRoamingPackage(
    selectedPackage?.minContractDur,
    selectedPackage?.minContractDurUnit,
  );

  const packageName = stripFlatName(selectedPackage?.shortName);

  const handleSuccess = useCallback(() => {
    setOptionBasket(undefined);
    // Added in order to trigger a refetching in the DeviceManagement
    queryClient
      .invalidateQueries({ queryKey: ["/sim/roamingInfo"] })
      .then(() => {
        showAlert({
          type: AlertTypes.success,
          text: t("cockpit.managementTile.roaming.checkout.success"),
        });
        navigate(`../display?simId=${simId}`, { replace: true });
      });
  }, [navigate, queryClient, setOptionBasket, showAlert, simId, t]);

  useEffect(() => {
    if (isPackagesError) {
      handleError("Failed to load roaming packages");
      navigate(`../booking?simId=${simId}`, { replace: true });
    }
  }, [isPackagesError, handleError, navigate, simId]);

  useEffect(() => {
    if (!isLoadingPackages && availableRoamingPackages && !selectedPackage) {
      setOptionBasket(undefined);
      navigate(`../booking?simId=${simId}`, { replace: true });
    }
  }, [
    isLoadingPackages,
    availableRoamingPackages,
    selectedPackage,
    setOptionBasket,
    navigate,
    simId,
  ]);

  const packageSelectionPath = `../booking?simId=${simId}`;
  const goToPackageSelection = useCallback(() => {
    navigate(packageSelectionPath, { replace: true });
  }, [navigate, packageSelectionPath]);

  const refreshOptionBasket = useCallback(() => {
    orderRoamingPackage(Number(simId), { optionId })
      .then((data) => {
        if (!data?.basket) {
          throw new Error("Empty basket response from orderRoamingPackage");
        }

        setOptionBasket(data.basket);
      })
      .catch((error) => {
        handleError(error);
        goToPackageSelection();
      });
  }, [goToPackageSelection, handleError, optionId, setOptionBasket, simId]);

  const paymentMode = usePaymentMode();
  const { isInvoice, isCreditCard, maskedCardNumberString, cardType } =
    paymentMode;

  const {
    submit,
    isSubmittingCheckout,
    datatransRequestDocument,
    isCreatingTransaction,
    isAddingOptionLoading,
    isAddingOptionDone,
  } = useOptionCheckoutSubmit({
    domain: "CheckoutRoamingOption",
    simId,
    basket: optionBasket,
    refreshBasket: refreshOptionBasket,
    paymentMode,
    onSuccess: handleSuccess,
  });

  const isSuccess = paymentStatus === "success";
  useEffect(() => {
    if (!isSubmittingCheckout && isSuccess) {
      handleSuccess();
    }
  }, [isSuccess, isSubmittingCheckout, handleSuccess]);

  useHandleCheckoutError(false);

  const cancel = () => {
    setOptionBasket(undefined);
    navigate(`../display?simId=${simId}`, { replace: true });
  };

  const { watch, control } = useForm<FormProps>();
  const watchedPaymentProvider = watch("paymentProvider");

  const disableSuccessButton =
    isCreatingTransaction ||
    isAddingOptionLoading ||
    isAddingOptionDone ||
    !(watchedPaymentProvider || isInvoice || isCreditCard);

  const packagePrice = option?.oneTimeCharges?.[0]?.amount;
  const discountAmount = optionBasket?.total?.oneTimeCampaign?.amount;
  const { campaignEndDate } = useGetCampaignEndDate(optionBasket);
  const credit = getCreditAmount(optionBasket?.balance);
  // substract discount and credit to achieve totalCost
  const totalOnce = (totalOnceAmount ?? 0) - (discountAmount || 0) - credit;
  const currency = getCurrencyFromLookupValue(
    optionBasket?.currency || { id: 1 },
  );

  return (
    <RoamingBookingPage
      dataTestid="checkout-roaming-option"
      title={tCheckout("title")}
      step={2}
      stepLabel={tCheckout("stepLabel")}
      onBack={cancel}
    >
      {isAddingOptionLoading && <LoadingSpinner />}
      {optionBasket && selectedPackage ? (
        <>
          <TwoColumnLayout
            rows={[
              {
                left: (
                  <div className="flex w-full flex-col gap-6">
                    <CardModule
                      dataTestid="roaming-checkout-summary"
                      variant="white"
                      bodyGap={6}
                      title={t("Common.checkout.summary")}
                    >
                      <div className="flex h-14 w-full flex-col justify-center rounded-md bg-surface-quaternary px-5 text-foreground-tertiary">
                        <p className="text-body-xs leading-md">
                          {t("portal:cockpit.managementTile.tabs.abo")}
                        </p>
                        <p className="truncate-safe text-body-base font-semibold leading-md">
                          {simDetail != null
                            ? getFlatNameFromDevice(simDetail, false)
                            : ""}
                        </p>
                      </div>

                      <div className="flex w-full flex-col gap-3">
                        <div className="flex w-full items-center gap-3">
                          <p className="truncate-safe min-w-0 flex-1 text-body-base font-semibold leading-none text-foreground-primary">
                            {tCheckout("roamingOption")}
                          </p>
                          <ButtonLink
                            data-testid="roaming-checkout-change"
                            size="sm"
                            variant="secondary"
                            to={packageSelectionPath}
                            replace
                          >
                            {t("portal:common.buttons.change")}
                          </ButtonLink>
                        </div>

                        <SubscriptionInfoBox>
                          <SubscriptionInfoRow
                            icon={<Globe className="size-6" />}
                            title={packageName ?? ""}
                          />
                        </SubscriptionInfoBox>
                      </div>
                    </CardModule>

                    <CheckoutPaymentCard
                      isInvoice={isInvoice}
                      isCreditCard={!!isCreditCard}
                      cardType={cardType}
                      maskedCardNumberString={maskedCardNumberString}
                      control={control}
                      sumIsZero={Math.max(totalOnce, 0) === 0}
                    />
                  </div>
                ),
                right: (
                  <CardModule
                    dataTestid="roaming-checkout-due"
                    className="self-start"
                    variant="white"
                    title={t("portal:common.checkout.nowToPay")}
                  >
                    <div className="flex w-full flex-col">
                      <PriceRow
                        withBackdrop
                        variant="strong"
                        label={
                          <>
                            <span>{packageName}</span>
                            {isYearlyPackage && (
                              <>
                                <br />
                                <span className="text-body-xs font-regular leading-md">
                                  {t("Common.tariff.365days")}
                                </span>
                              </>
                            )}
                          </>
                        }
                        value={
                          packagePrice != null
                            ? getCurrency(packagePrice, currency)
                            : ""
                        }
                      />

                      {!!discountAmount && discountAmount > 0 && (
                        <DeductionRow
                          withDivider={credit === 0}
                          label={
                            <DiscountLabel
                              name={t(
                                "Onboarding.sections.checkout.summary.discount",
                              )}
                              endDate={campaignEndDate}
                            />
                          }
                          amount={discountAmount}
                        />
                      )}

                      {credit > 0 && (
                        <DeductionRow
                          withDivider
                          label={t("portal:common.checkout.creditOther")}
                          amount={credit}
                        />
                      )}

                      <DueNowTotalRow
                        dataTestid="roaming-checkout-total"
                        label={t("portal:common.checkout.sum")}
                        amount={totalOnce}
                        currency={currency}
                        caption={tCheckout("once")}
                      />
                    </div>
                  </CardModule>
                ),
              },
            ]}
          />

          <LightboxWrapper
            paymentProvider={watchedPaymentProvider}
            transactionId={datatransRequestDocument?.refNo}
            sign={datatransRequestDocument?.sign}
            enabled={datatransRequestDocument !== undefined}
          />

          <div className="flex w-full justify-between gap-3">
            <ButtonLink
              data-testid="roaming-checkout-back-step"
              variant="secondary"
              to={packageSelectionPath}
              replace
            >
              {capitalizeFirstLetter(t("Common.label.back"))}
            </ButtonLink>
            <Button
              dataTestid="roaming-checkout-submit"
              disabled={disableSuccessButton}
              onClick={() => submit(totalOnceAmount)}
            >
              {t("Common.label.finish")}
            </Button>
          </div>
        </>
      ) : (
        <LoadingSpinner />
      )}
    </RoamingBookingPage>
  );
};
